import React, { useState, useEffect } from 'react';
import { List, Datagrid, TextField, TextInput, Filter, Show, SimpleShowLayout, ShowButton, FunctionField, ReferenceField, DateTimeInput, AutocompleteInput } from 'react-admin';
import { Create, SimpleForm, usePermissions, BooleanInput, BooleanField, ReferenceInput, SelectInput } from 'react-admin';
import { cloneElement } from 'react';
import {
    useListContext,
    TopToolbar,
    CreateButton,
    ExportButton,
    EditButton,
    Edit,
    SaveButton,
    Toolbar,
    sanitizeListRestProps,
} from 'react-admin';

const styles = {
    div1: {
        display: 'block',
        width: "25%",
        float: "left",
        position: "flex",
        marginRight: "10px"
    },
    div1Mobile: {
        display: 'block',
        width: "50%",
        float: "left",
        position: "flex",
        marginLeft: "2%",
        marginRight: "2%"
    },
    div2: {
        display: 'block',
        width: "74%",
        float: "left",
        position: "flex"
    },
    div2Mobile: {
        display: 'block',
        width: "100%",
        float: "left",
        marginLeft: "2%",
        marginRight: "2%",
        marginTop: "2%",
        position: "flex"
    },
    table: {
        overflowX: "auto"
    }
};

const ListActions = (props) => {
    const {
        className,
        exporter,
        filters,
        maxResults,
        ...rest
    } = props;
    const {
        currentSort,
        resource,
        displayedFilters,
        filterValues,
        basePath,
        showFilter,
        total,
    } = useListContext();
    const permissions = usePermissions().permissions;
    if (!permissions) {
        return null;
    }
    return (
        <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
            {filters && cloneElement(filters, {
                resource,
                showFilter,
                displayedFilters,
                filterValues,
                context: 'button',
            })}
            {permissions.includes("messages.create") &&
                <CreateButton basePath={basePath} label="Crear" />
            }
            {permissions.includes("messages.export") &&
                <ExportButton
                    label="Exportar csv"
                    disabled={total === 0}
                    resource={resource}
                    sort={currentSort}
                    filterValues={filterValues}
                    maxResults={maxResults}
                />
            }
            {/* Add your custom actions */}
        </TopToolbar>
    );
};

const MessageTitle = ({ record, optionTitle }) => {
    if (optionTitle) {
        return ""
    }
    return <span>Mensaje - {record ? `${record.title}` : ''}</span>;
};

export const MessagesList = props => {
    const permissions = usePermissions().permissions;
    if (!permissions) {
        return null;
    }
    return (
        <List filters={<MessageFilter />} {...props} actions={<ListActions />} >
            <Datagrid rowClick="show">
                <ShowButton label="Ver" />
                <TextField source="title" label="Título" />
                <TextField source="content" label='Contenido' />
                <TextField source="from" label="Fecha inicio" />
                <TextField source="to" label="Fecha inicio" />
                {(permissions.includes("buildings.list")) &&
                    <ReferenceField label="Edificio" source="buildingId" reference="buildings" link="show">
                        <FunctionField render={record => `${record.address}`} />
                    </ReferenceField>
                }
                {permissions.includes("users.list") &&
                    <ReferenceField source="userId" label='Usuario' reference="users">
                        <FunctionField label="Nombre completo" render={record => `${record.firstname} ${record.lastname}`} link="show" />
                    </ReferenceField>
                }
                <BooleanField source="active" label="Activo" />
            </Datagrid>
        </List>
    )
};

const MessageShowActions = ({ basePath, data, resource, edit = false, f = false }) => {
    const permissions = usePermissions().permissions;
    if (!permissions) {
        return null;
    }
    if (edit && permissions.includes("messages.edit")) {
        return (
            <TopToolbar>
                {(edit) ? <EditButton basePath={basePath} record={data} label="Editar mensaje" /> : null}
            </TopToolbar>
        )
    }
    return (<p></p>)
};

export const MessagesShow = props => {
    const [width, setWindowWidth] = useState(0)

    useEffect(() => {

        updateDimensions();

        window.addEventListener("resize", updateDimensions);
        return () =>
            window.removeEventListener("resize", updateDimensions);
    }, [])
    const updateDimensions = () => {
        const width = window.innerWidth
        setWindowWidth(width)
    }

    const responsive = {
        web: width > 1023
    }

    const permissions = usePermissions().permissions;
    if (!permissions) {
        return null;
    }

    return (
        <div>
            <div style={(responsive.web) ? styles.div1 : styles.div1Mobile}>
                <Show title={<MessageTitle />} actions={<MessageShowActions edit={true} />} {...props}>
                    <SimpleShowLayout >
                        <TextField source="title" label="Título" />
                        <TextField source="content" label='Contenido' />
                        <TextField source="from" label="Fecha inicio" />
                        <TextField source="to" label="Fecha inicio" />
                        {(permissions.includes("buildings.list")) &&
                            <ReferenceField label="Edificio" source="buildingId" reference="buildings" link="show">
                                <FunctionField render={record => `${record.address}`} />
                            </ReferenceField>
                        }
                        {permissions.includes("users.list") &&
                            <ReferenceField source="userId" label='Usuario' reference="users">
                                <FunctionField label="Nombre completo" render={record => `${record.firstname} ${record.lastname}`} link="show" />
                            </ReferenceField>
                        }
                        <BooleanField source="active" label="Activo" />
                    </SimpleShowLayout>
                </Show>
            </div>
        </div>
    )
};

const MessageFilter = (props) => (
    <Filter {...props}>
        <TextInput label="Buscar" source="q" alwaysOn />
    </Filter>
);

export const MessagesCreate = props => {
    const permissions = usePermissions().permissions;
    if (!permissions) {
        return null;
    }
    return (
    <Create {...props}>
        <SimpleForm submitOnEnter={false}>
            <TextInput source="title" label='Título (opcional)' />
            <TextInput source="content" label='Contenido' />
            <DateTimeInput source="from" label='Fecha de inicio' />
            <DateTimeInput source="to" label='Fecha de término' />
            <BooleanInput source='all_buildings' label='Todos los edificios' />
            {permissions.includes("buildings.list") &&
                <ReferenceInput source="buildingId" label='Edificio' reference="buildings" perPage={200} allowEmpty
                    sort={{ field: 'id', order: 'DESC' }}
                >
                    <SelectInput optionText={record => `${record.id}.- ${record.address}`} />
                </ReferenceInput>
            }
            {permissions.includes("users.list") &&
                <ReferenceInput
                    label="Usuario (aún no disponible)" source="userId" reference="users"
                    allowEmpty
                    sort={{ field: 'firstname', order: 'ASC' }}
                    optionValue="username"
                    suggestionLimit={10}
                    alwaysOn>
                    <AutocompleteInput optionText="filtro" />
                </ReferenceInput>
            }
            <BooleanInput source='active' label='Activo' />
        </SimpleForm>
    </Create>
)};

export const MessagesEdit = props => {
    const permissions = usePermissions().permissions;
    if (!permissions) {
        return null;
    }
    return (
    <Edit {...props}>
        <SimpleForm submitOnEnter={false} toolbar={<MessageEditToolbar />}>
            <TextInput source="title" label='Título (opcional)' />
            <TextInput source="content" label='Contenido' />
            <DateTimeInput source="from" label='Fecha de inicio' />
            <DateTimeInput source="to" label='Fecha de término' />
            <BooleanInput source='all_buildings' label='Todos los edificios' />
            {permissions.includes("buildings.list") &&
                <ReferenceInput source="buildingId" label='Edificio' reference="buildings" perPage={200}
                    sort={{ field: 'id', order: 'DESC' }}
                >
                    <SelectInput optionText={record => `${record.id}.- ${record.address}`} />
                </ReferenceInput>
            }
            {permissions.includes("users.list") &&
                <ReferenceInput
                    label="Usuario (aún no disponible)" source="userId" reference="users"
                    allowEmpty
                    sort={{ field: 'firstname', order: 'ASC' }}
                    optionValue="username"
                    suggestionLimit={10}
                    alwaysOn>
                    <AutocompleteInput optionText="filtro" />
                </ReferenceInput>
            }
            <BooleanInput source='active' label='Activo' />
        </SimpleForm>
    </Edit>
)};

const MessageEditToolbar = props => (
    <Toolbar {...props} >
        <SaveButton />
    </Toolbar>
);
